import { Link } from 'react-router'
import { Button, Card, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { StatTile } from '@/components/StatTile'
import { PageHeader } from '@/app/layouts/PageHeader'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { BRAND } from '@/brand/brand'

const HOLDINGS = [
  { symbol: 'USDC', name: 'USD Coin', amount: '4,120.00', value: '$4,120.00', change: '0.00%', up: null },
  { symbol: 'BTC', name: 'Bitcoin', amount: '0.0412', value: '$2,811.49', change: '+2.12%', up: true },
  { symbol: 'ETH', name: 'Ethereum', amount: '0.9120', value: '$3,395.39', change: '−0.87%', up: false },
] as const

export function CryptoWalletScreen() {
  return (
    <>
      <PageHeader
        title="Crypto wallet"
        subtitle={`Self-custody balances that settle into your ${BRAND.name} account. 0.10% to convert.`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="south_west" to="/crypto/receive">Receive</Button>
            <Button variant="primary" size="md" iconStart="add" to="/crypto/buy">Buy</Button>
          </>
        }
      />

      <BentoGrid>
        {[
          { label: 'Wallet value', value: '$10,326.88', note: '+1.4% in 24h' },
          { label: 'Stablecoin', value: '$4,120.00', note: '40% of the wallet' },
          { label: 'Convert fee', value: '0.10%', note: `To any ${BRAND.name} balance` },
          { label: 'Network', value: 'Base · Solana', note: 'Gas paid at cost' },
        ].map((stat) => (
          <StatTile key={stat.label} stat={stat} />
        ))}

        <BentoSpan span={3}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">Holdings</Text>
              <Pill tone="neutral">{HOLDINGS.length}</Pill>
            </Card.Header>
            <Stack gap={0} className="pb-mt-3">
              {HOLDINGS.map((holding) => (
                <Link key={holding.symbol} to={`/crypto/${holding.symbol}`} className={checkout.line} style={{ width: '100%' }}>
                  {holding.symbol === 'USDC' ? (
                    <CurrencyBadge currency="USDC" size={34} />
                  ) : (
                    <Card tone="dark" padded={false} className="pb-coin">
                      <Text variant="eyebrow" weight="bold" tone="accent">{holding.symbol}</Text>
                    </Card>
                  )}
                  <Stack grow gap={0}>
                    <Text variant="caption" weight="semibold">{holding.name}</Text>
                    <Text variant="caption" tone="secondary" numeric>{holding.amount} {holding.symbol}</Text>
                  </Stack>
                  <Stack align="end" gap={0}>
                    <Text variant="caption" weight="semibold" numeric>{holding.value}</Text>
                    <Text
                      variant="eyebrow"
                      weight="bold"
                      numeric
                      tone={holding.up === null ? 'secondary' : holding.up ? 'positive' : 'negative'}
                    >
                      {holding.change}
                    </Text>
                  </Stack>
                </Link>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        <Card tone="dark">
          <Text variant="caption" weight="semibold" tone="onDark">Convert to spendable</Text>
          <Text variant="caption" tone="onDarkMuted" className="pb-mt-2">
            Move USDC into your USD balance and spend it with the card. It settles instantly and
            keeps earning until you do.
          </Text>
          <Stack gap={3} className="pb-mt-5">
            {[['Convert fee', '0.10%'], ['Arrives', 'Instantly'], ['Minimum', '$10']].map(([label, value]) => (
              <Stack key={label} direction="row" justify="between" align="center">
                <Text variant="caption" tone="onDarkMuted">{label}</Text>
                <Text variant="caption" weight="semibold" tone="onDark" numeric>{value}</Text>
              </Stack>
            ))}
          </Stack>
          <Card.Footer>
            <Button size="md" block iconEnd="arrow_forward" to="/crypto/convert">Convert USDC</Button>
          </Card.Footer>
        </Card>
      </BentoGrid>
    </>
  )
}
